// F3 · BUDDY TRAINER · vertical slice UI (Preview only)
// Mobile-first, minimal. Consumes /api/buddy-trainer/session/* endpoints.
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Bot, Send, RefreshCw, HelpCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BuddyTrainer() {
  const [session, setSession] = useState(null);
  const [objective, setObjective] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [showWhy, setShowWhy] = useState(false);
  const [showSubjectPicker, setShowSubjectPicker] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const bottomRef = useRef(null);
  const student_id = localStorage.getItem("user_id") || localStorage.getItem("email") || "preview_user";

  useEffect(() => {
    startSession();
    axios.get(`${API}/api/buddy-trainer/subjects`).then(r => setSubjects(r.data.subjects || [])).catch(() => {});
  }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  async function startSession() {
    setStatus("starting"); setError(null); setShowSubjectPicker(false);
    try {
      const r = await axios.post(`${API}/api/buddy-trainer/session/start`, {
        student_id, days_to_exam: null,
      });
      setSession({ id: r.data.session_id, status: "ACTIVE" });
      setObjective(r.data.objective);
      setMessages(r.data.conversation.map(t => ({ role: t.role, text: t.text })));
      setStatus("idle");
    } catch (e) { setError("No pudimos iniciar la sesión. Reintenta."); setStatus("error"); }
  }

  async function changeSubject(newSubjectId) {
    if (!session) return;
    setStatus("starting"); setError(null); setShowSubjectPicker(false);
    try {
      const r = await axios.post(`${API}/api/buddy-trainer/session/${session.id}/change-subject`, {
        new_subject_id: newSubjectId,
      });
      setSession({ id: r.data.session_id, status: "ACTIVE" });
      setObjective(r.data.objective);
      setMessages(r.data.conversation.map(t => ({ role: t.role, text: t.text })));
      setStatus("idle");
    } catch (e) { setError("No pudimos cambiar la materia."); setStatus("error"); }
  }

  async function sendTurn(text) {
    if (!session || !text.trim()) return;
    setMessages(m => [...m, { role: "STUDENT", text }]);
    setInput(""); setStatus("typing");
    try {
      const r = await axios.post(`${API}/api/buddy-trainer/session/${session.id}/turn`, { message: text });
      setMessages(m => [...m, { role: "BUDDY", text: r.data.turn.text, strategy: r.data.turn.strategy }]);
      if (r.data.status !== "ACTIVE") {
        setStatus("done");
        setSession(s => ({ ...s, status: r.data.status, outcome: r.data.outcome }));
      } else { setStatus("idle"); }
    } catch (e) { setError("No pudimos enviar tu mensaje."); setStatus("error"); }
  }

  const submit = (e) => { e.preventDefault(); sendTurn(input); };
  const canType = status === "idle";
  const otherSubjects = subjects.filter(s => s.id !== objective?.subject_id);

  return (
    <div className="max-w-2xl mx-auto p-4 flex flex-col gap-4" data-testid="buddy-trainer-root">
      <header className="flex items-start gap-3">
        <div className="rounded-full bg-emerald-100 p-2"><Bot className="w-6 h-6 text-emerald-700" /></div>
        <div>
          <h1 className="text-xl font-semibold">BUDDY TRAINER</h1>
          {objective && (
            <p className="text-sm text-neutral-600" data-testid="buddy-objective-label">
              Hoy: <b>{objective.primary_skill_id.replace(/_/g, " ")}</b> · {objective.subject_id.replace(/_/g, " ")}
            </p>
          )}
        </div>
      </header>

      {objective && (
        <div className="flex flex-wrap gap-2 text-xs">
          <button onClick={() => setShowWhy(v => !v)} className="px-3 py-1 rounded-full border" data-testid="buddy-why-btn">
            <HelpCircle className="w-3 h-3 inline mr-1" /> ¿Por qué este objetivo?
          </button>
          <button onClick={() => setShowSubjectPicker(true)} className="px-3 py-1 rounded-full border" data-testid="buddy-restart-btn">
            <RefreshCw className="w-3 h-3 inline mr-1" /> Prefiero otra materia
          </button>
        </div>
      )}

      {showSubjectPicker && (
        <div className="bg-neutral-50 border rounded-lg p-3 text-sm" data-testid="buddy-subject-picker">
          <p className="font-medium mb-2">¿Con cuál materia prefieres trabajar?</p>
          <div className="grid grid-cols-1 gap-2">
            {otherSubjects.map(s => (
              <button
                key={s.id}
                onClick={() => changeSubject(s.id)}
                className="text-left px-3 py-2 rounded border hover:bg-white"
                data-testid={`buddy-subject-pick-${s.id}`}
              >{s.name}</button>
            ))}
          </div>
          <button onClick={() => setShowSubjectPicker(false)} className="mt-2 text-xs text-neutral-500 underline">Cancelar</button>
        </div>
      )}

      {showWhy && objective && (
        <div className="bg-neutral-50 border rounded-lg p-3 text-sm" data-testid="buddy-why-panel">
          <p className="font-medium mb-1">Razones:</p>
          <ul className="list-disc pl-5">{objective.reason_codes.map(r => <li key={r}>{r.replace(/_/g, " ").toLowerCase()}</li>)}</ul>
        </div>
      )}

      <div className="flex-1 space-y-2 overflow-y-auto max-h-[60vh] pb-4" data-testid="buddy-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "STUDENT" ? "justify-end" : "justify-start"}`}>
            <div className={`rounded-2xl px-3 py-2 max-w-[85%] text-sm ${m.role === "STUDENT" ? "bg-emerald-600 text-white" : "bg-neutral-100"}`} data-testid={`buddy-msg-${m.role.toLowerCase()}-${i}`}>
              {m.text}
            </div>
          </div>
        ))}
        {status === "typing" && (
          <div className="text-neutral-500 text-xs italic pl-2" data-testid="buddy-typing">Buddy escribiendo…</div>
        )}
        {error && <div className="text-red-600 text-sm" data-testid="buddy-error">{error}</div>}
        {status === "done" && session?.outcome && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm space-y-2" data-testid="buddy-outcome">
            <p className="font-medium">Buen trabajo por hoy</p>
            <p>{session.outcome.recommended_next_action}</p>
            <div className="flex gap-2 pt-1">
              <button onClick={startSession} className="px-3 py-1 rounded-full bg-emerald-600 text-white text-xs" data-testid="buddy-continue-btn">
                Continuar entrenando
              </button>
              <a href="/avanzo" className="px-3 py-1 rounded-full border text-xs" data-testid="buddy-home-btn">
                Volver al inicio
              </a>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={submit} className="flex gap-2 sticky bottom-0 bg-white pt-2">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Escribe tu respuesta…"
          disabled={!canType}
          className="flex-1 border rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="buddy-input"
        />
        <button type="submit" disabled={!canType || !input.trim()} className="rounded-full bg-emerald-600 text-white px-4 py-2 disabled:opacity-40" data-testid="buddy-send-btn">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
